"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { curveLinear } from "@visx/curve";
import XYChart from "@/components/chart/components/XYChart";
import type { ChartConfig } from "@/components/chart/types";
import { getChartSeriesColors } from "@/components/chart/styles/colors";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { useIsDarkMode } from "@/utils/colorScheme";
import {
  buildTimeseriesChartData,
  CHAT_ENTITY_CARD_SHELL_CLASS,
  CHAT_TIMESERIES_XY_CONFIG,
  formatTimeseriesCaption,
} from "./chatAnalyticsEmbedUi";
import { ChatTimeseriesLegendFooter } from "./ChatTimeseriesLegendFooter";
import {
  buildPerformancePlaygroundUrl,
  type ChatPlaygroundOpenPayload,
} from "@/utils/playground";
import { useOpenUrl } from "@/components/ui/os-layout/openUrl";

/** Plot area height (legend is rendered outside XYChart). */
const CHART_PLOT_HEIGHT_PX = 248;

type ChatTimeseriesChartCardProps = {
  embed: ChatTimeseriesChartEmbed;
};

export function ChatTimeseriesChartCard({ embed }: ChatTimeseriesChartCardProps) {
  const openUrl = useOpenUrl();
  const isDark = useIsDarkMode();
  const chartTheme = isDark ? "dark" : "light";
  const { chartColors: companyChartColors } = useCompanyColors();
  const chartColors = getChartSeriesColors(companyChartColors);
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const applyWidth = (width: number) => {
      if (width > 0) {
        setChartWidth(width);
      }
    };

    applyWidth(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? element.clientWidth;
      applyWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { rows: chartData, series: chartSeries } = useMemo(
    () => buildTimeseriesChartData(embed.series),
    [embed.series],
  );

  const chartConfig = useMemo((): Partial<ChartConfig> => {
    return {
      ...CHAT_TIMESERIES_XY_CONFIG,
      theme: chartTheme,
      xKey: "x",
      yKeys: chartSeries.map((s) => s.key),
      series: chartSeries.map((s, index) => ({
        key: s.key,
        type: "line",
        label: s.label,
        color: chartColors[index % chartColors.length],
        curve: curveLinear,
      })),
      axes: embed.y_unit ? { y: { title: embed.y_unit } } : undefined,
    };
  }, [chartColors, chartSeries, chartTheme, embed.y_unit]);

  const legendItems = useMemo(
    () =>
      chartSeries.map((s, index) => ({
        label: s.label,
        value: 0,
        color: chartColors[index % chartColors.length],
        showValue: false,
      })),
    [chartColors, chartSeries],
  );

  const caption = useMemo(() => formatTimeseriesCaption(embed), [embed]);
  const showLegendBar = chartSeries.length > 1 || embed.series.some((s) => s.stream?.label);
  const playgroundUrl = useMemo(() => {
    const open = embed.playground_open;
    if (!open?.assets?.length) return null;
    return buildPerformancePlaygroundUrl(open as ChatPlaygroundOpenPayload);
  }, [embed.playground_open]);

  const handleOpenPlayground = () => {
    if (!playgroundUrl) return;
    openUrl(playgroundUrl, { title: "Playground" });
  };

  return (
    <div className={clsx(CHAT_ENTITY_CARD_SHELL_CLASS, "text-sm")} data-testid="chat-embed-timeseries-chart">
      {embed.title ? (
        <div className="mb-2 flex items-start justify-between gap-2 px-0.5">
          <div className="text-[12px] font-medium leading-tight text-slate-700 dark:text-slate-200">
            {embed.title}
          </div>
          {playgroundUrl ? (
            <button
              type="button"
              onClick={handleOpenPlayground}
              className="shrink-0 text-[11px] font-medium text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
              data-testid="chat-embed-open-playground"
            >
              Open in Playground
            </button>
          ) : null}
        </div>
      ) : playgroundUrl ? (
        <div className="mb-2 flex justify-end px-0.5">
          <button
            type="button"
            onClick={handleOpenPlayground}
            className="text-[11px] font-medium text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
            data-testid="chat-embed-open-playground"
          >
            Open in Playground
          </button>
        </div>
      ) : null}
      {caption ? (
        <div
          className="-mt-1 mb-2 px-0.5 text-[11px] leading-snug text-slate-500 dark:text-slate-400"
          data-testid="chat-embed-chart-caption"
        >
          {caption}
        </div>
      ) : null}
      <div
        ref={containerRef}
        className={clsx(
          "w-full shrink-0 overflow-hidden rounded-md border px-1 pt-1",
          isDark
            ? "border-slate-700/45 bg-slate-900/80"
            : "border-slate-200/60 bg-white",
        )}
        data-theme={chartTheme}
        data-testid="chat-embed-chart-surface"
        style={{ minHeight: CHART_PLOT_HEIGHT_PX + 4 }}
      >
        {chartWidth > 0 ? (
          <XYChart
            data={chartData}
            config={chartConfig}
            width={chartWidth}
            height={CHART_PLOT_HEIGHT_PX}
          />
        ) : null}
      </div>
      {showLegendBar ? <ChatTimeseriesLegendFooter items={legendItems} /> : null}
    </div>
  );
}
